"use client";

import { motion } from "framer-motion";
import { ChevronDown, ArrowRight } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import { InlineVideo } from "@/components/components/InlineVideo";

export default function ScrollyHero({ message = "Learn About Thinknado" }: { message?: string }) {
  const pathname = usePathname();
  const router = useRouter();

  const handleTellMeMore = () => {
    if (pathname === "/") {
      const el = document.getElementById("demo");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      router.push("/#demo");
    }
  };

  const handleTryItOut = () => {
    if (pathname.includes("/app/session")) {
      const el = document.getElementById("demo");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      router.push("/app/session#demo");
    }
  };

  return (
    <section
      className="relative bg-black text-white flex flex-col justify-center min-h-[calc(100dvh-120px)] sm:min-h-[calc(100dvh-160px)] lg:min-h-[calc(100dvh-204px)]"
    >
      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(245,255,255,0.15) 1px, transparent 1px), linear-gradient(to bottom, rgba(245,255,255,0.15) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <div className="relative w-full max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="flex items-center gap-12">
        <motion.div
          className="stack gap-6 flex-[0_0_60%]"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.12 } },
          }}
        >
          <motion.p
            className="font-mono text-[11px] font-bold uppercase tracking-[0.25em]"
            style={{ color: "#F50078" }}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.215, 0.61, 0.355, 1] } },
            }}
          >
            Welcome to Thinknado
          </motion.p>

          <motion.h1
            className="type-6 font-black uppercase leading-[0.88] type-display text-white"
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.215, 0.61, 0.355, 1] } },
            }}
          >
            Where Thinking
            <br />
            Is Amplified
          </motion.h1>

          <motion.p
            className="text-sm text-white/50 max-w-xl leading-relaxed"
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.215, 0.61, 0.355, 1] } },
            }}
          >
            Only 5 steps to go from mundane to insane.
          </motion.p>

          <motion.div
            className="flex flex-wrap items-center gap-3"
            variants={{
              hidden: { opacity: 0, y: 16 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.215, 0.61, 0.355, 1] } },
            }}
          >
            <button
              onClick={handleTellMeMore}
              className="inline-flex items-center gap-2 border border-white/20 px-5 py-3 text-[11px] uppercase tracking-[0.2em] font-semibold text-white/70 hover:text-white hover:border-white/50 transition-colors"
            >
              Tell Me More
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={handleTryItOut}
              className="inline-flex items-center gap-2 border border-white/25 text-white px-5 py-3 text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-white hover:text-black hover:border-white transition-colors"
            >
              Try It Out
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </motion.div>
        </motion.div>
        <div className="flex justify-center lg:col-start-7 lg:col-end-13 self-center items-center w-full">
          <InlineVideo src="/videos/better-swirl.mp4" />
        </div>
        </div>
      </div>

      {/* Scroll hint */}
      <motion.div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.6 }}
      >
        <span className="text-[11px] uppercase tracking-[0.2em]">{message}</span>
        <ChevronDown className="w-4 h-4 animate-bounce" />
      </motion.div>
    </section>
  );
}
